import { useState } from "react";
import { isConfigured, loadConfig, saveConfig } from "./api";
import type { AppConfig } from "./types";
import Dashboard from "./views/Dashboard";
import GradedView from "./views/GradedView";
import PurchaseOrders from "./views/PurchaseOrders";
import SalesView from "./views/SalesView";
import SealedView from "./views/SealedView";
import SinglesView from "./views/SinglesView";

type View = "dashboard" | "sealed" | "singles" | "graded" | "purchases" | "sales" | "settings";

const NAV: { id: View; label: string; icon: string }[] = [
  { id: "dashboard",  label: "Dashboard",   icon: "◈" },
  { id: "sealed",     label: "Sealed",      icon: "◻" },
  { id: "singles",    label: "Singles",     icon: "▤" },
  { id: "graded",     label: "Graded",      icon: "★" },
  { id: "purchases",  label: "Purchases",   icon: "⊕" },
  { id: "sales",      label: "Sales",       icon: "$" },
  { id: "settings",   label: "Settings",    icon: "⚙" },
];

export default function App() {
  const [cfg, setCfg] = useState<AppConfig>(loadConfig);
  const [view, setView] = useState<View>(isConfigured(loadConfig()) ? "dashboard" : "settings");

  function handleSave(next: AppConfig) {
    saveConfig(next);
    setCfg(next);
    if (view === "settings") setView("dashboard");
  }

  const configured = isConfigured(cfg);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-mark">CI</div>
        <span className="topbar-title">Card Inventory</span>
        {!configured && (
          <span className="topbar-sub" style={{ color: "var(--warn)" }}>
            Not configured — open Settings
          </span>
        )}
        {configured && (
          <span className="topbar-sub">{cfg.apiUrl}</span>
        )}
      </header>

      <nav className="nav">
        {NAV.map((n) => (
          <button
            key={n.id}
            className={`nav-item ${view === n.id ? "active" : ""}`}
            onClick={() => setView(n.id)}
          >
            <span className="nav-icon">{n.icon}</span>
            {n.label}
          </button>
        ))}
      </nav>

      <main className="main">
        {!configured && view !== "settings" ? (
          <div className="state-container">
            <div className="state-box">
              <h3>One more step</h3>
              <p>Enter your API URL and token in Settings to get started.</p>
              <button className="btn btn-primary" onClick={() => setView("settings")}>
                Open Settings
              </button>
            </div>
          </div>
        ) : view === "settings" ? (
          <SettingsView cfg={cfg} onSave={handleSave} />
        ) : view === "dashboard" ? (
          <Dashboard cfg={cfg} />
        ) : view === "sealed" ? (
          <SealedView cfg={cfg} />
        ) : view === "singles" ? (
          <SinglesView cfg={cfg} />
        ) : view === "graded" ? (
          <GradedView cfg={cfg} />
        ) : view === "purchases" ? (
          <PurchaseOrders cfg={cfg} />
        ) : view === "sales" ? (
          <SalesView cfg={cfg} />
        ) : null}
      </main>
    </div>
  );
}

function SettingsView({ cfg, onSave }: { cfg: AppConfig; onSave: (c: AppConfig) => void }) {
  const [apiUrl, setApiUrl] = useState(cfg.apiUrl);
  const [apiToken, setApiToken] = useState(cfg.apiToken);
  const [saved, setSaved] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSave({ apiUrl: apiUrl.replace(/\/$/, ""), apiToken });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div className="settings-wrap">
      <form className="settings-form" onSubmit={handleSubmit}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Settings</h2>
        <div className="form-group">
          <label className="form-label">API URL</label>
          <input
            className="form-input"
            value={apiUrl}
            onChange={(e) => setApiUrl(e.target.value)}
            placeholder="https://inventory-api.futuregadgetlabs.com"
          />
          <span className="form-hint">Backend base URL (no trailing slash)</span>
        </div>
        <div className="form-group">
          <label className="form-label">API Token</label>
          <input
            className="form-input"
            type="password"
            value={apiToken}
            onChange={(e) => setApiToken(e.target.value)}
            placeholder="Bearer token"
          />
          <span className="form-hint">Stored in localStorage — never sent anywhere except your API</span>
        </div>
        <div className="settings-save-row">
          <button className="btn btn-primary" type="submit">Save</button>
          {saved && <span className="save-ok">Saved!</span>}
        </div>
      </form>
    </div>
  );
}
